/**
 * An enumeration of entrance types.
 *
 * @readonly
 * @property {Symbol} NO_ACCOUNTS - Neither Auth0 nor Bettermode Community Hub account on entrance to the app.
 * @property {Symbol} AUTH0_ONLY - Has Auth0 account but no Bettermode Community Hub account on entrance to the app.
 * @property {Symbol} BOTH_ACCOUNTS - Has both Auth0 and Bettermode Community Hub account on entrance to the app.
 */
export const EntranceTypes = Object.freeze({
  NO_ACCOUNTS: Symbol("none"),
  AUTH0_ONLY: Symbol("auth0_only"),
  BOTH_ACCOUNTS: Symbol("both")
});

/**
 * Retrieves user data from the 'SESSpartauth' cookie.
 *
 * @returns {Object|null} The parsed user data object if the cookie exists and is valid, otherwise null.
 * @throws {Error} If there is an error parsing the cookie data.
 */
export const getUserCookieData = () => {
  const cookieValRaw = document.cookie.match(/SESSpartauth=([^;]+)/);
  const cookieVal = cookieValRaw ? decodeURIComponent(cookieValRaw[1]) : null;

  try {
    const user = JSON.parse(cookieVal);
    return user;
  } catch (e) {
    throw new Error("Error parsing user cookie data: ", e);
  }
};

/**
 * Determines the entrance type based on user data and screen parameter value when noHandshake is false.
 *
 * @param {Object} userData - The user data object.
 * @param {string} screenParamValue - The screen parameter value.
 * @returns {Symbol} The entrance type.
 */
const determineEntranceTypeWithHandshake = (userData, screenParamValue) => {
  if (screenParamValue === "signin") {
    return EntranceTypes.BOTH_ACCOUNTS;
  }
  if (userData?.hasAuth0Acct) {
    return EntranceTypes.AUTH0_ONLY;
  }
  return EntranceTypes.NO_ACCOUNTS;
};

/**
 * Determines the entrance type based on user data and screen parameter value when noHandshake is true.
 *
 * @param {Object} userData - The user data object.
 * @param {string} screenParamValue - The screen parameter value.
 * @returns {Symbol} The entrance type.
 */
const determineEntranceTypeWithoutHandshake = (userData) => {
  const { hasAuth0Acct = false, hasBettermodeAcct = false } = userData || {};
  if (hasAuth0Acct && hasBettermodeAcct) {
    return EntranceTypes.BOTH_ACCOUNTS;
  }
  if (hasAuth0Acct) {
    return EntranceTypes.AUTH0_ONLY;
  }
  return EntranceTypes.NO_ACCOUNTS;
};

/**
 * Gets the entrance type based on user data, screen parameter value, and noHandshake flag.
 *
 * @param {Object} userData - The user data object.
 * @param {string} screenParamValue - The screen parameter value.
 * @param {boolean} noHandshake - The noHandshake flag.
 * @returns {Symbol} The entrance type.
 */
export const getEntranceType = (userData, screenParamValue, noHandshake) => {
  if (noHandshake) {
    return determineEntranceTypeWithoutHandshake(userData);
  } else {
    return determineEntranceTypeWithHandshake(userData, screenParamValue);
  }
};

/**
 * A function that returns a promise that resolves after a specified delay.
 *
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {Promise} A promise that resolves after the specified delay.
 */
export const delay = async (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * A function that returns a callback function that resolves after a specified delay.
 * This is useful for delaying the execution of a callback function.
 *
 * @param {number} ms - The number of milliseconds to delay.
 * @returns {Function} A callback function that resolves after the specified delay.
 */
export const delayCallback =
  (ms, callback = () => {}) =>
  async () =>
    delay(ms).then(callback);

/**
 * Checks if a given string is a valid HTML string.
 *
 * @param {string} str The string to check.
 * @returns {boolean} True if the string is a valid HTML string, false otherwise.
 */
export function isHTMLString(str) {
  if (!str || typeof str !== "string") return false;

  const parser = new DOMParser();
  const doc = parser.parseFromString(str, "text/html");

  // Check if there are valid HTML elements
  return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1 || node.nodeType === 3);
}
